<i18n>
{
	"en": {
		"theAddress": "the address",
		"cancelLabel": "Cancel",
		"saveAddress": "Save Address",
		"title": "Edit Address",
		"name": "Name",
		"street": "Street",
		"postalPlace": "City",
		"postalCode": "Postal Code",
		"countryCode": "Country Code",
		"failedToFetch": "Failed to search for city by postal code",
		"failedToSave": "Failed to save new address",
		"savedNewAddress": "New address saved",
		"gs1LocationNumber": "GS1 Location Number",
		"contactName": "Contact Name",
		"contactPhone": "Phone Number",
		"contactEmail": "Email",
		"website": "Website",
		"customsCreditNumber": "Customs Credit Number",
		"vat": "MVA",
		"voec": "VOEC",
		"organizationNumber": "Organization Number",
		"exporterNumber": "Exporter Number",
		"eori": "EORI",
		"customer": "Customer",
		"vendor": "Vendor",
		"marks": "Marks"
	},
	"no": {
		"theAddress": "adressen",
		"cancelLabel": "Avbryt",
		"saveAddress": "Lagre adresse",
		"title": "Rediger adresse",
		"name": "Navn",
		"street": "Gate",
		"postalPlace": "Poststed",
		"postalCode": "Postnummer",
		"countryCode": "Landkode",
		"failedToFetch": "Kunne ikke søke etter poststed med postnummer",
		"failedToSave": "Kunne ikke lagre ny adresse",
		"savedNewAddress": "Ny adresse lagret",
		"gs1LocationNumber": "GS1 Locno.",
		"contactName": "Kontakt navn",
		"contactPhone": "Telefon",
		"contactEmail": "E-post",
		"website": "Nettside",
		"customsCreditNumber": "Tollkredittnummber",
		"vat": "MVA",
		"voec": "VOEC",
		"organizationNumber": "Foretaksnummer",
		"exporterNumber": "Eksportør nummer",
		"eori": "EORI",
		"customer": "Kunde",
		"vendor": "Leverandør",
		"marks": "Merknader"
	}
}
</i18n>

<template>
	<t-dialog
		:icon="icon"
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('saveAddress')"
		:cancelLabel="$t('cancelLabel')"
		:isConfirmDisabled="!canSave"
		disableCloseOnConfirm
		iconType="bp"
		width="800px"
		focusFirstOnShow
		:keyListeners="getKeyListeners()"
		@closing="checkClose"
		@ok="onOk"
		@close="onClose"
		@shown="onDialogShow"
	>
		<div v-if="editableAddress" class="edit-address">
			<t-pane v-bind="paneOptions" class="main-address">
				<t-text-input
					ref="addressName"
					v-model="editableAddress.name"
					:label="$t('name')"
					:isInvalid="errors.invalidName"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.street"
					:label="$t('street')"
					@input="setDirty"
				/>

				<t-text-input
					v-model="editableAddress.street2"
					style="margin-top: 5px"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.street3"
					style="margin-top: 5px"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.street4"
					style="margin-top: 5px"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.street5"
					style="margin-top: 5px"
					@input="setDirty"
				/>

				<div class="countryPostalGroup">
					<t-text-input
						v-model="editableAddress.countryCode"
						:label="$t('countryCode')"
						@input="fetchGlobalPostalPlace"
						@change="forceGlobalPostalPlace"
					/>
					<t-text-input
						v-model="editableAddress.postalCode"
						:label="$t('postalCode')"
						:isInvalid="errors.invalidPostalCode"
						@input="fetchGlobalPostalPlace"
						@change="forceGlobalPostalPlace"
					/>
					<t-text-input v-model="editableAddress.postalPlace" :label="$t('postalPlace')" @input="cancelFetch"/>
				</div>
				<t-text-input
					v-model="editableAddress.gs1LocationNumber"
					:label="$t('gs1LocationNumber')"
					@input="setDirty"
				/>
			</t-pane>
			<t-pane v-bind="paneOptions" class="contact">
				<t-text-input
					v-model="editableAddress.contactName"
					:label="$t('contactName')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.contactPhone"
					:label="$t('contactPhone')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.contactEmail"
					:label="$t('contactEmail')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.website"
					:label="$t('website')"
					@input="setDirty"
				/>
			</t-pane>
			<t-pane v-bind="paneOptions" class="external-ids">
				<t-text-input
					v-model="editableAddress.customsCreditNumber"
					:label="$t('customsCreditNumber')"
					@input="setDirty"
				/>
				<t-checkbox
					v-model="editableAddress.vat"
					:label="$t('vat')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.voec"
					:label="$t('voec')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.organizationNumber"
					:label="$t('organizationNumber')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.exporterNumber"
					:label="$t('exporterNumber')"
					@input="setDirty"
				/>
				<t-text-input
					v-model="editableAddress.eori"
					:label="$t('eori')"
					@input="setDirty"
				/>
			</t-pane>
			<t-pane v-bind="paneOptions" class="customer-and-vendor">
				<CustomerInput
					ref="customer"
					:value="customer"
					disableButtonTabbing
					hidePadding
					@change="onChangeCustomer"
				/>
				<VendorInput
					ref="vendor"
					:label="$t('vendor')"
					:value="vendor"
					@change="onChangeVendor"
				/>
			</t-pane>

			<t-text-area
				v-model="editableAddress.marks"
				class="marks"
				:label="$t('marks')"
				@input="setDirty"
			/>
		</div>

		<ConfirmUnsavedChanges
			:isShown="confirmUnsavedChanges"
			:subject="$t('theAddress')"
			@cancel="toggleUnsavedChanges(false)"
			@save="onOk"
			@close="onCloseDirty"
		/>
	</t-dialog>
</template>

<script>
import TTextInput from '@/components/evergreen/inputs/TTextInput';
import { mapState, mapActions } from '../store/globalAddresses.store.js';
import { copyParty } from '@/core';
import ConfirmUnsavedChanges from '@/components/modals/ConfirmUnsavedChanges';
import VendorInput from '@/modules/vendors/VendorInput.vue';
import { mapActions as mapCustomerActions } from '@/modules/customers/store/customers.store.js';
import { mapActions as mapVendorActions } from '@/modules/vendors/store/vendors.store.js';
import CustomerInput from '@/modules/customers/CustomerInput';

export default {
	components: { CustomerInput, TTextInput, ConfirmUnsavedChanges, VendorInput },
	props: {
		address: Object,
		icon: String,
		isShown: Boolean,
	},
	data() {
		return {
			editableAddress: undefined,
			fetchTimeout: undefined,
			paneOptions: {
				border: 'default',
				background: 'tint1',
				borderRadius: '5px',
				style: { padding: '5px', }
			},
			confirmUnsavedChanges: false,
			dirty: false,
			closeDirty: false,
			customer: {},
			vendor: {},
		};
	},

	computed: {
		...mapState(['errors']),
		canSave() {
			return this.editableAddress && this.editableAddress.name && this.editableAddress.postalCode;
		},
	},

	methods: {
		...mapActions([
			'addNewGlobalAddress',
			'getGlobalPostalPlace',
		]),
		...mapCustomerActions(['getCustomerByNumber']),
		...mapVendorActions(['getVendorByNumber']),
		getKeyListeners() {
			return [
				{ 	key: 'S',
					listener: this.onOk,
					modifiers: { ctrlKey: true, shiftKey: undefined, altKey: false, stop: true, prevent: true } },
				{ 	key: 'Enter',
					listener: this.onOk,
					modifiers: { ctrlKey: true, shiftKey: false, altKey: false, stop: true, prevent: true } },
			];
		},
		onClose() {
			this.toggleUnsavedChanges(false);
			this.$emit('close');
			this.dirty = false;
		},
		setupEditableAddress() {
			this.editableAddress = copyParty(this.address);
			this.dirty = false;
		},
		async onOk() {
			try {
				await this.addNewGlobalAddress({ originalAddress: this.address, newAddress: this.editableAddress });
				this.dirty = false;
				this.onClose();
				this.$notify({
					type: 'success',
					title: this.$t('savedNewAddress')
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToSave'),
					duration: -1
				});
			}
			this.$emit('ok', this.editableAddress);
		},

		processGlobalPostalPlace() {
			this.getGlobalPostalPlace(this.editableAddress)
				.then(globalPostalPlace => {
					if (globalPostalPlace.postalPlace) {
						this.editableAddress.postalPlace = globalPostalPlace.postalPlace;
					}
					if (globalPostalPlace.countryCode) {
						this.editableAddress.countryCode = globalPostalPlace.countryCode;
					}
					this.setDirty();
					this.$forceUpdate();
				})
				.catch ( () => {
					this.$notify({
						type: 'error',
						title: this.$t('failedToFetch')
					});
				});
		},
		fetchGlobalPostalPlace() {
			clearTimeout(this.fetchTimeout);
			this.fetchTimeout = setTimeout(() => {
				this.processGlobalPostalPlace();
			},200);
		},
		cancelFetch() {
			this.dirty = true;
			clearTimeout(this.fetchTimeout);
		},
		forceGlobalPostalPlace() {
			clearTimeout(this.fetchTimeout);
			this.processGlobalPostalPlace();
		},
		setDirty() {
			this.dirty = true;
		},
		onCloseDirty() {
			this.closeDirty = true;
			this.onClose();
		},
		toggleUnsavedChanges(value) {
			this.confirmUnsavedChanges = value;
		},
		checkClose(abort) {
			if (this.dirty && !this.closeDirty) {
				abort(true);
				this.toggleUnsavedChanges(true);
			} else {
				abort(false);
			}
			this.closeDirty = false;
		},

		onChangeCustomer(event) {
			this.editableAddress.customerNumber = event.customerNo;
			this.setDirty();
			this.setCustomer();
			if (Object.keys(event).length) {
				this.focusNext({ target: this.$refs.customer.$el.querySelector('input') });
			}
		},

		onChangeVendor(event) {
			this.editableAddress.vendorNumber = event.vendorNumber;
			this.setDirty();
			this.setVendor();
			if (Object.keys(event).length) {
				this.focusNext({ target: this.$refs.vendor.$el.querySelector('input') });
			}
		},

		onDialogShow() {
			this.setupEditableAddress();
			this.setCustomer();
			this.setVendor();
		},

		async setCustomer() {
			this.customer = await this.getCustomer();
		},
		async getCustomer() {
			if (!this.editableAddress.customerNumber) return {};
			let customer = await this.getCustomerByNumber(this.editableAddress.customerNumber);
			if (!customer) return {};
			return customer;
		},

		async setVendor() {
			this.vendor = await this.getVendor();
		},
		async getVendor() {
			if (!this.editableAddress.vendorNumber) return {};
			let vendor = await this.getVendorByNumber(this.editableAddress.vendorNumber);
			if (!vendor) return {};
			return vendor;
		},
	},
};
</script>

<style lang="scss" scoped>
.edit-address {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-areas:
		"topLeft	topRight"
		"bottomLeft bottomRight";
	grid-column-gap: 15px;
	grid-row-gap: 15px;
	.marks {
		grid-column: 1/3;
	}
}

.main-address {
	grid-area: topLeft;
	display: grid;
	row-gap: 5px;

	.countryPostalGroup {
		display: grid;
		grid-template-columns: 95px 95px 1fr;
		grid-column-gap: 5px;
	}
}

.contact {
	grid-area: bottomLeft;
}

.external-ids {
	grid-area: topRight;
}

.customer-and-vendor {
	grid-area: bottomRight;
}
</style>
